import React, { useState, useEffect } from 'react';
import {
  Video,
  X,
  Circle,
  Square,
  Pause,
  Play,
  Upload,
  Trash2,
  Monitor,
  Camera,
  Mic,
  MicOff,
  VideoOff,
} from 'lucide-react';
import { useScreenRecorder } from '../../hooks/useScreenRecorder';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import {
  minutosAproximadosNoTeto,
  nomeDoArquivoDeVideo,
  QUALIDADE_PADRAO,
  QUALIDADES,
  type QualidadeDeVideo
} from '../../hooks/screenRecorderFormat';

interface ScreenRecorderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendVideo: (videoFile: File) => Promise<void>;
}

type RecorderState = 'setup' | 'recording' | 'preview';

const ScreenRecorderModal: React.FC<ScreenRecorderModalProps> = ({
  isOpen,
  onClose,
  onSendVideo,
}) => {
  const [state, setState] = useState<RecorderState>('setup');
  const [includeWebcam, setIncludeWebcam] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [quality, setQuality] = useState<QualidadeDeVideo>(QUALIDADE_PADRAO);
  const [isSending, setIsSending] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const toast = useToast();
  const { confirm } = useConfirm();

  const {
    isRecording,
    isPaused,
    recordingTime,
    videoBlob,
    error,
    bytesRecorded,
    maxBytes,
    stoppedBySizeLimit,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
  } = useScreenRecorder();

  // Limpar preview URL quando fechar
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // O estado da tela segue o do gravador: se a permissão for negada, a tela
  // fica no setup mostrando o erro, em vez de ir para "Gravando..." à toa.
  useEffect(() => {
    if (isRecording) {
      setState('recording');
      return;
    }
    if (videoBlob && videoBlob.size > 0) {
      setState('preview');
      setPreviewUrl(URL.createObjectURL(videoBlob));
    }
  }, [videoBlob, isRecording]);

  const handleStartRecording = async () => {
    await startRecording({ includeWebcam, audioEnabled, quality });
  };

  const handleStopRecording = async () => {
    await stopRecording();
  };

  const handleSendVideo = async () => {
    if (!videoBlob) return;

    try {
      setIsSending(true);

      // Nome e tipo seguem o formato gravado de fato (webm, ou mp4 no Safari).
      const tipo = videoBlob.type || 'video/webm';
      const file = new File([videoBlob], nomeDoArquivoDeVideo(tipo, new Date()), { type: tipo });

      await onSendVideo(file);

      // Fechar modal e limpar
      handleClose();
    } catch (err) {
      console.error('Erro ao enviar vídeo:', err);
      toast.error('Erro ao enviar vídeo', 'Por favor, tente novamente.');
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = async () => {
    if (isRecording) {
      const confirmClose = await confirm({
        message: 'Tem certeza que deseja fechar? A gravação será cancelada.',
        variant: 'danger',
      });
      if (!confirmClose) return;
      cancelRecording();
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    setState('setup');
    setIncludeWebcam(false);
    setAudioEnabled(true);
    setQuality(QUALIDADE_PADRAO);
    onClose();
  };

  const handleDiscard = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    cancelRecording();
    setState('setup');
  };

  const formatMB = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  const usoDoTeto = Math.min(100, (bytesRecorded / maxBytes) * 100);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-coral" />
            <h2 className="text-lg font-semibold text-gray-800">
              {state === 'setup' && 'Gravar Tela'}
              {state === 'recording' && 'Gravando...'}
              {state === 'preview' && 'Preview da Gravação'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isSending}
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Setup State */}
          {state === 'setup' && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-coral-soft rounded-full mb-4">
                  <Monitor className="w-10 h-10 text-coral" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Preparar Gravação
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Configure as opções de gravação e clique em iniciar. Você
                  poderá escolher qual tela gravar na próxima etapa.
                </p>
              </div>

              <div className="space-y-4">
                {/* Qualidade: troca nitidez por duração dentro do limite do chat */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-800 mb-3">Qualidade</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Qualidade do vídeo">
                    {(Object.keys(QUALIDADES) as QualidadeDeVideo[]).map((chave) => {
                      const perfil = QUALIDADES[chave];
                      const selecionada = quality === chave;
                      return (
                        <button
                          key={chave}
                          type="button"
                          role="radio"
                          aria-checked={selecionada}
                          onClick={() => setQuality(chave)}
                          className={`p-3 rounded-lg border-2 text-left transition-colors ${
                            selecionada
                              ? 'border-coral bg-coral-soft'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <p className="font-semibold text-gray-800">{perfil.rotulo}</p>
                          <p className="text-sm text-coral font-medium">
                            até ~{minutosAproximadosNoTeto(chave)} min
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{perfil.descricao}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Opção Webcam */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        includeWebcam ? 'bg-coral-soft' : 'bg-gray-200'
                      }`}
                    >
                      {includeWebcam ? (
                        <Camera className="w-5 h-5 text-coral" />
                      ) : (
                        <VideoOff className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Incluir Webcam</p>
                      <p className="text-sm text-gray-600">
                        Exibir sua câmera no canto da tela
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeWebcam}
                      onChange={(e) => setIncludeWebcam(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-coral-soft rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-coral"></div>
                  </label>
                </div>

                {/* Opção Áudio */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        audioEnabled ? 'bg-coral-soft' : 'bg-gray-200'
                      }`}
                    >
                      {audioEnabled ? (
                        <Mic className="w-5 h-5 text-coral" />
                      ) : (
                        <MicOff className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Gravar Áudio</p>
                      <p className="text-sm text-gray-600">
                        Incluir áudio do sistema
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={audioEnabled}
                      onChange={(e) => setAudioEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-coral-soft rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-coral"></div>
                  </label>
                </div>
              </div>

              <button
                onClick={handleStartRecording}
                className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <Circle className="w-5 h-5 fill-current" />
                Iniciar Gravação
              </button>
            </div>
          )}

          {/* Recording State */}
          {state === 'recording' && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4 relative">
                  <Circle className="w-10 h-10 text-red-600 fill-current animate-pulse" />
                  {isPaused && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 rounded-full">
                      <Pause className="w-8 h-8 text-white" />
                    </div>
                  )}
                </div>

                <div className="text-4xl font-bold text-gray-800 mb-2 font-mono">
                  {formatTime(recordingTime)}
                </div>

                <p className="text-gray-600">
                  {isPaused
                    ? 'Gravação pausada'
                    : 'Gravando sua tela...'}
                </p>

                <div className="max-w-xs mx-auto mt-4">
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${usoDoTeto >= 85 ? 'bg-red-500' : 'bg-coral'}`}
                      style={{ width: `${usoDoTeto}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatMB(bytesRecorded)} de {formatMB(maxBytes)} — para sozinha ao chegar no limite
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                {!isPaused ? (
                  <button
                    onClick={pauseRecording}
                    className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Pause className="w-5 h-5" />
                    Pausar
                  </button>
                ) : (
                  <button
                    onClick={resumeRecording}
                    className="btn-primary-lg"
                  >
                    <Play className="w-5 h-5 fill-current" />
                    Retomar
                  </button>
                )}

                <button
                  onClick={handleStopRecording}
                  className="btn-danger"
                >
                  <Square className="w-5 h-5 fill-current" />
                  Parar
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Dica:</strong> Você também pode parar a gravação
                  clicando no botão "Parar compartilhamento" na barra do
                  navegador.
                </p>
              </div>
            </div>
          )}

          {/* Preview State */}
          {state === 'preview' && previewUrl && (
            <div className="space-y-6">
              <div className="bg-gray-900 rounded-lg overflow-hidden">
                <video
                  src={previewUrl}
                  controls
                  className="w-full"
                  style={{ maxHeight: '400px' }}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-800">Gravação Concluída</p>
                  <p className="text-sm text-gray-600">
                    Duração: {formatTime(recordingTime)} · {formatMB(videoBlob?.size ?? 0)}
                  </p>
                  {stoppedBySizeLimit && (
                    <p className="text-sm text-amber-700 mt-1">
                      A gravação parou sozinha ao chegar no limite de tamanho do chat.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleDiscard}
                  className="btn-secondary flex-1"
                  disabled={isSending}
                >
                  <Trash2 className="w-5 h-5" />
                  Descartar
                </button>

                <button
                  onClick={handleSendVideo}
                  className="btn-primary flex-1"
                  disabled={isSending}
                >
                  {isSending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Enviar no Chat
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreenRecorderModal;
